import { createStore, del as idbDel, get as idbGet, keys as idbKeys, set as idbSet } from 'idb-keyval';
import { getOnStorage, getLocalStorageKeyForTable, removeFromStorage } from './storageFunctions';
import { buildCartDetailsKey, DEFAULT_CART_BRAND_KEY, getCartBrandKeyFromStorage } from './cartStorageKeys';
import type { CartDetailsProps } from '@/hooks/CartContext';

export type CartDetailsMap = Record<string, CartDetailsProps>;

const STORE_DB_NAME = 'BF_SELLER_CARTS';
const STORE_OBJECT_NAME = 'cart_details';
const KEY_PREFIX = 'cart:';
const LEGACY_BLOB_KEY = 'cart_details';
const MIGRATION_MARKER_KEY = '__cart_details_idb_v2_per_cart';
const SCOPED_KEYS_MARKER = '__cart_details_idb_v3_scoped_keys';

const isMetaKey = (key: string) => key.startsWith('__');

export interface CartDetailsBackend {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const idbStore = createStore(STORE_DB_NAME, STORE_OBJECT_NAME);

const createIdbBackend = (): CartDetailsBackend => {
  const store = idbStore;
  return {
    getItem: async (key) => {
      const v = await idbGet<string>(key, store);
      return v ?? null;
    },
    setItem: async (key, value) => {
      await idbSet(key, value, store);
    },
    removeItem: async (key) => {
      await idbDel(key, store);
    },
    keys: async () => {
      const all = await idbKeys(store);
      return all.filter((k): k is string => typeof k === 'string');
    },
  };
};

let backend: CartDetailsBackend | null = null;

export const __setCartDetailsBackendForTests = (testBackend: CartDetailsBackend | null) => {
  backend = testBackend;
};

const getBackend = (): CartDetailsBackend => {
  if (!backend) backend = createIdbBackend();
  return backend;
};

const cartRowKey = (scopeKey: string) => `${KEY_PREFIX}${scopeKey}`;

const scopeKeyFromRowKey = (rowKey: string): string | null => {
  if (!rowKey.startsWith(KEY_PREFIX)) return null;
  return rowKey.slice(KEY_PREFIX.length);
};

const parseDetail = (raw: string | null): CartDetailsProps | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CartDetailsProps;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (!Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const parseMap = (raw: string | null): CartDetailsMap | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CartDetailsMap;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const mergeCartDetailsMaps = (a: CartDetailsMap, b: CartDetailsMap): CartDetailsMap => {
  const out: CartDetailsMap = { ...a };
  for (const [key, entry] of Object.entries(b)) {
    const existing = out[key];
    const existingCount = existing?.items?.length ?? 0;
    const incomingCount = entry?.items?.length ?? 0;
    if (!existing || incomingCount > existingCount) {
      out[key] = entry;
    }
  }
  return out;
};

export const isLegacyClientOnlyCartKey = (key: string): boolean => /^\d+$/.test(key);

const resolveBrandKeyForLegacyRemap = (): string => {
  try {
    return getCartBrandKeyFromStorage() || DEFAULT_CART_BRAND_KEY;
  } catch {
    return DEFAULT_CART_BRAND_KEY;
  }
};

/**
 * Staging/versões antigas usavam `COD_CLIENTE` como chave.
 * Master (multiloja) usa `COD_CLIENTE:brandKey`. Remapeia chaves só-numéricas
 * para o escopo da bandeira ativa (ou `default_brand`).
 */
export const remapLegacyClientOnlyCartKeys = (
  map: CartDetailsMap,
  brandKey: string = resolveBrandKeyForLegacyRemap()
): CartDetailsMap => {
  const out: CartDetailsMap = {};

  for (const [key, detail] of Object.entries(map)) {
    if (!detail) continue;

    const targetKey = isLegacyClientOnlyCartKey(key)
      ? buildCartDetailsKey(Number(key), brandKey)
      : key;

    if (!targetKey) continue;

    const existing = out[targetKey];
    const existingCount = existing?.items?.length ?? 0;
    const incomingCount = detail.items?.length ?? 0;
    if (!existing || incomingCount > existingCount) {
      out[targetKey] = detail;
    }
  }

  return out;
};

const readLegacyLocalStorage = (): CartDetailsMap => getOnStorage('cart_details') || {};

const clearLegacyLocalStorage = (): void => {
  try {
    removeFromStorage('cart_details');
  } catch {
    /* legado opcional */
  }
};

const readLegacyBlobFromIndexedDb = async (): Promise<CartDetailsMap | null> => {
  const raw = await getBackend().getItem(LEGACY_BLOB_KEY);
  return parseMap(raw);
};

const writeCartRows = async (data: CartDetailsMap): Promise<void> => {
  for (const [scopeKey, detail] of Object.entries(data)) {
    await getBackend().setItem(cartRowKey(scopeKey), JSON.stringify(detail));
  }
};

const deleteLegacyBlob = async (): Promise<void> => {
  try {
    await getBackend().removeItem(LEGACY_BLOB_KEY);
  } catch {
    /* blob legado opcional */
  }
};

const readAllCartRows = async (): Promise<CartDetailsMap> => {
  const map: CartDetailsMap = {};
  const allKeys = await getBackend().keys();
  for (const rowKey of allKeys) {
    if (isMetaKey(rowKey) || rowKey === LEGACY_BLOB_KEY) continue;
    const scopeKey = scopeKeyFromRowKey(rowKey);
    if (!scopeKey) continue;
    const detail = parseDetail(await getBackend().getItem(rowKey));
    if (detail) map[scopeKey] = detail;
  }
  return map;
};

const syncFullMapToRows = async (data: CartDetailsMap): Promise<void> => {
  const incoming = new Set(Object.keys(data).map(cartRowKey));
  const existing = (await getBackend().keys()).filter((k) => k.startsWith(KEY_PREFIX));

  await writeCartRows(data);

  for (const rowKey of existing) {
    if (!incoming.has(rowKey)) {
      await getBackend().removeItem(rowKey);
    }
  }
};

const hasLegacyClientOnlyKeys = (map: CartDetailsMap): boolean =>
  Object.keys(map).some(isLegacyClientOnlyCartKey);

export const migrateLegacyCartDetails = async (): Promise<{ migrated: boolean; alreadyDone: boolean }> => {
  const store = getBackend();
  const v2Marker = await store.getItem(MIGRATION_MARKER_KEY);
  const v3Marker = await store.getItem(SCOPED_KEYS_MARKER);

  const fromLocal = readLegacyLocalStorage();
  const fromBlob = await readLegacyBlobFromIndexedDb();
  const fromRows = await readAllCartRows();

  const needsV2 =
    v2Marker !== '1' ||
    Object.keys(fromLocal).length > 0 ||
    !!(fromBlob && Object.keys(fromBlob).length > 0);

  let final = mergeCartDetailsMaps(fromRows, mergeCartDetailsMaps(fromBlob ?? {}, fromLocal));
  const hadLegacySources =
    Object.keys(fromLocal).length > 0 || !!(fromBlob && Object.keys(fromBlob).length > 0);

  const needsScopedRemap = v3Marker !== '1' || hasLegacyClientOnlyKeys(final);

  if (!needsV2 && !needsScopedRemap && v2Marker === '1' && v3Marker === '1') {
    return { migrated: false, alreadyDone: true };
  }

  if (needsScopedRemap) {
    final = remapLegacyClientOnlyCartKeys(final);
  }

  if (Object.keys(final).length > 0) {
    await syncFullMapToRows(final);
  } else if (needsScopedRemap && hasLegacyClientOnlyKeys(fromRows)) {
    await syncFullMapToRows({});
  }

  await deleteLegacyBlob();
  await store.setItem(MIGRATION_MARKER_KEY, '1');
  await store.setItem(SCOPED_KEYS_MARKER, '1');

  if (Object.keys(fromLocal).length > 0) {
    clearLegacyLocalStorage();
  }

  return {
    migrated: hadLegacySources || needsScopedRemap,
    alreadyDone: false,
  };
};

/**
 * Importa as linhas do banco de carrinhos **anterior à renomeação** (`PWA_RCA_CARTS`).
 *
 * O formato da linha é idêntico ao daqui — a única diferença entre as duas versões do app é o nome
 * do banco — então não há conversão a fazer: só reidentificar a chave de escopo, remapear o que
 * ainda estiver na convenção pré-multiloja (`COD_CLIENTE` puro) e mesclar com o que já existe.
 *
 * Os marcadores `__*` do banco antigo ficam de fora de propósito: eles descrevem migrações que
 * rodaram *naquele* banco. Sem eles, `migrateLegacyCartDetails` faz sua própria passada aqui — que
 * é idempotente e é justamente quem cobre o caso do blob `cart_details` de versões mais antigas.
 *
 * A mesclagem usa a mesma regra do resto do módulo (`mergeCartDetailsMaps`: vence quem tem mais
 * itens) e grava com `writeCartRows`, não com `syncFullMapToRows` — este apagaria os carrinhos que
 * já estão no banco novo e não vieram do antigo.
 *
 * @returns quantos carrinhos legados foram recuperados.
 */
export const importLegacyCartRows = async (rows: Record<string, string>): Promise<number> => {
  const deLinhas: CartDetailsMap = {};
  let deBlob: CartDetailsMap = {};

  for (const [rowKey, raw] of Object.entries(rows)) {
    if (isMetaKey(rowKey)) continue;

    if (rowKey === LEGACY_BLOB_KEY) {
      deBlob = parseMap(raw) ?? {};
      continue;
    }

    const scopeKey = scopeKeyFromRowKey(rowKey);
    if (!scopeKey) continue;

    const detail = parseDetail(raw);
    if (detail) deLinhas[scopeKey] = detail;
  }

  const legados = remapLegacyClientOnlyCartKeys(mergeCartDetailsMaps(deBlob, deLinhas));
  const total = Object.keys(legados).length;
  if (!total) return 0;

  await writeCartRows(mergeCartDetailsMaps(await readAllCartRows(), legados));
  return total;
};

export const getCartDetails = async (): Promise<CartDetailsMap> => {
  await migrateLegacyCartDetails();
  const rows = await readAllCartRows();
  if (!hasLegacyClientOnlyKeys(rows)) return rows;

  const remapped = remapLegacyClientOnlyCartKeys(rows);
  await syncFullMapToRows(remapped);
  await getBackend().setItem(SCOPED_KEYS_MARKER, '1');
  return remapped;
};

export const getCartDetail = async (scopeKey: string): Promise<CartDetailsProps | null> => {
  await migrateLegacyCartDetails();
  const direct = parseDetail(await getBackend().getItem(cartRowKey(scopeKey)));
  if (direct) return direct;

  if (!scopeKey.includes(':')) return null;

  const clientId = scopeKey.split(':')[0];
  if (!isLegacyClientOnlyCartKey(clientId)) return null;

  const legacy = parseDetail(await getBackend().getItem(cartRowKey(clientId)));
  if (!legacy) return null;

  await getBackend().setItem(cartRowKey(scopeKey), JSON.stringify(legacy));
  await getBackend().removeItem(cartRowKey(clientId));
  return legacy;
};

export const setCartDetail = async (scopeKey: string, detail: CartDetailsProps): Promise<boolean> => {
  try {
    await migrateLegacyCartDetails();
    await getBackend().setItem(cartRowKey(scopeKey), JSON.stringify(detail));
    return true;
  } catch (error) {
    console.error('[cartStore] Falha ao persistir carrinho no IndexedDB:', scopeKey, error);
    return false;
  }
};

export const removeCartDetail = async (scopeKey: string): Promise<boolean> => {
  try {
    await migrateLegacyCartDetails();
    await getBackend().removeItem(cartRowKey(scopeKey));
    return true;
  } catch (error) {
    console.error('[cartStore] Falha ao remover carrinho no IndexedDB:', scopeKey, error);
    return false;
  }
};

export const setCartDetails = async (data: CartDetailsMap): Promise<boolean> => {
  try {
    await migrateLegacyCartDetails();
    await syncFullMapToRows(remapLegacyClientOnlyCartKeys(data));
    return true;
  } catch (error) {
    console.error('[cartStore] Falha ao persistir carrinhos no IndexedDB:', error);
    return false;
  }
};

export const getLegacyCartDetailsStorageKey = (): string => getLocalStorageKeyForTable('cart_details');
